import Matter from "./matter.js";

class TagCloud {
  constructor(container) {
    this.canvas =
      typeof container === "string"
        ? document.querySelector(container)
        : container;

    this.engine = null;
    this.world = null;
    this.runner = null;
    this.mouse = null;
    this.mouseConstraint = null;
    this.walls = [];
    this.tagPairs = [];
    this.width = 0;
    this.height = 0;
    this.initialized = false;
    this.destroyed = false;

    this.viewportThreshold = 0.15;
    this.fallDelayMs = 1000;
    this.fallDelayTimeout = null;

    this.wallThickness = 200;
    this.edgePadding = 12;
    this.maxSpeed = 12;

    this.intersectionObserver = null;
    this.resizeObserver = null;

    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    this._onAfterUpdate = () => {
      this._clampAllBodies();
      this._syncDom();
    };

    this._onStartDrag = () => {
      this.canvas.style.touchAction = "none";
    };

    this._onEndDrag = () => {
      this.canvas.style.touchAction = "pan-y";
    };
  }

  init() {
    if (!this.canvas || this.destroyed || this.initialized) {
      return;
    }

    this.initialized = true;

    const section = this.canvas.closest(".section--tag-cloud") ?? this.canvas;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          this.intersectionObserver?.disconnect();
          this.intersectionObserver = null;

          this.fallDelayTimeout = window.setTimeout(() => {
            this.fallDelayTimeout = null;

            if (this.destroyed) {
              return;
            }

            this.canvas.classList.add("is-active");

            if (this.motionQuery.matches) {
              this._startStatic();
              return;
            }

            this._startSimulation();
          }, this.fallDelayMs);
        });
      },
      { threshold: this.viewportThreshold },
    );

    this.intersectionObserver.observe(section);
  }

  _getTagSpawnBounds() {
    this._updateBounds();
    const headerReserve = Math.min(this.height * 0.42, 320);

    return {
      minX: 24,
      maxX: Math.max(this.width - 24, 24),
      minY: headerReserve,
      maxY: Math.max(this.height - 24, headerReserve + 24),
    };
  }

  _startStatic() {
    const tags = [...this.canvas.querySelectorAll(".tag")];
    const bounds = this._getTagSpawnBounds();

    tags.forEach((el, index) => {
      const cols = Math.ceil(Math.sqrt(tags.length));
      const col = index % cols;
      const row = Math.floor(index / cols);
      const cellW = (bounds.maxX - bounds.minX) / cols;
      const cellH = (bounds.maxY - bounds.minY) / Math.ceil(tags.length / cols);

      const x = bounds.minX + cellW * col + cellW * 0.5;
      const y = bounds.minY + cellH * row + cellH * 0.5;

      el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(0rad)`;
    });
  }

  _allowPageScroll(mouse) {
    if (!mouse?.element || typeof mouse.mousewheel !== "function") {
      return;
    }

    mouse.element.removeEventListener("mousewheel", mouse.mousewheel);
    mouse.element.removeEventListener("DOMMouseScroll", mouse.mousewheel);
    mouse.element.removeEventListener("wheel", mouse.mousewheel);
  }

  _startSimulation() {
    if (this.engine || this.destroyed) {
      return;
    }

    const { Engine, Runner, World, Mouse, MouseConstraint, Events } = Matter;

    this.engine = Engine.create({
      positionIterations: 10,
      velocityIterations: 10,
    });
    this.world = this.engine.world;
    this.runner = Runner.create();

    this._updateBounds();
    this._createWalls();
    this._createBodies();
    this._clampAllBodies();
    this._syncDom();

    this.mouse = Mouse.create(this.canvas);
    this._allowPageScroll(this.mouse);

    this.mouseConstraint = MouseConstraint.create(this.engine, {
      mouse: this.mouse,
      constraint: {
        stiffness: 0.2,
      },
    });

    World.add(this.world, this.mouseConstraint);

    Events.on(this.mouseConstraint, "startdrag", this._onStartDrag);
    Events.on(this.mouseConstraint, "enddrag", this._onEndDrag);
    Events.on(this.engine, "afterUpdate", this._onAfterUpdate);
    Runner.run(this.runner, this.engine);

    this.resizeObserver = new ResizeObserver(() => {
      this._handleResize();
    });
    this.resizeObserver.observe(this.canvas);
  }

  _updateBounds() {
    this.width = this.canvas.clientWidth;
    this.height = this.canvas.clientHeight;
  }

  _getBodyHalfExtents(body) {
    const { min, max } = body.bounds;

    return {
      halfW: (max.x - min.x) / 2,
      halfH: (max.y - min.y) / 2,
    };
  }

  _getPlayBounds(body) {
    const { halfW, halfH } = this._getBodyHalfExtents(body);
    const inset = this.edgePadding;

    return {
      minX: inset + halfW,
      maxX: Math.max(this.width - inset - halfW, inset + halfW),
      minY: inset + halfH,
      maxY: Math.max(this.height - inset - halfH, inset + halfH),
    };
  }

  _clampBody(body) {
    const { Body } = Matter;
    const bounds = this._getPlayBounds(body);
    let { x, y } = body.position;
    let clamped = false;

    if (x < bounds.minX) {
      x = bounds.minX;
      clamped = true;
    } else if (x > bounds.maxX) {
      x = bounds.maxX;
      clamped = true;
    }

    if (y > bounds.maxY) {
      y = bounds.maxY;
      clamped = true;
    } else if (y < bounds.minY && body.velocity.y < 0) {
      y = bounds.minY;
      clamped = true;
    }

    if (clamped) {
      Body.setPosition(body, { x, y });

      Body.setVelocity(body, {
        x: body.velocity.x * 0.25,
        y: body.velocity.y * 0.25,
      });

      Body.setAngularVelocity(body, body.angularVelocity * 0.25);
    }

    const speed = Math.hypot(body.velocity.x, body.velocity.y);

    if (speed > this.maxSpeed) {
      const scale = this.maxSpeed / speed;

      Body.setVelocity(body, {
        x: body.velocity.x * scale,
        y: body.velocity.y * scale,
      });
    }
  }

  _clampAllBodies() {
    this.tagPairs.forEach(({ body }) => {
      this._clampBody(body);
    });
  }

  _createWalls() {
    const { Bodies, World } = Matter;
    const { width, height, wallThickness } = this;
    const half = wallThickness / 2;
    const spanW = width + wallThickness * 2;
    const spanH = height + wallThickness * 2;

    this.walls = [
      Bodies.rectangle(width / 2, height + half, spanW, wallThickness, {
        isStatic: true,
        friction: 1,
        restitution: 0,
      }),
      Bodies.rectangle(-half, height / 2, wallThickness, spanH, {
        isStatic: true,
        friction: 1,
        restitution: 0,
      }),
      Bodies.rectangle(width + half, height / 2, wallThickness, spanH, {
        isStatic: true,
        friction: 1,
        restitution: 0,
      }),
      Bodies.rectangle(width / 2, -half, spanW, wallThickness, {
        isStatic: true,
        friction: 1,
        restitution: 0,
      }),
    ];

    World.add(this.world, this.walls);
  }

  _createBodies() {
    const { Bodies, World } = Matter;
    const tags = [...this.canvas.querySelectorAll(".tag")];
    const bounds = this._getTagSpawnBounds();

    this.tagPairs = tags.map((el, index) => {
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      const x =
        Math.random() * Math.max(bounds.maxX - bounds.minX - width, 0) +
        bounds.minX +
        width / 2;
      const dropOffset = 100 + index * 20 + Math.random() * 120;
      const y = bounds.minY - height / 2 - dropOffset;

      const body = Bodies.rectangle(x, y, width, height, {
        chamfer: { radius: height / 2 },
        density: 0.01,
        friction: 0.2,
        frictionAir: 0.02,
        restitution: 0.35,
        slop: 0.02,
      });

      World.add(this.world, body);
      return { body, el };
    });
  }

  _syncDom() {
    this.tagPairs.forEach(({ body, el }) => {
      const { x, y } = body.position;

      el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${body.angle}rad)`;
    });
  }

  _handleResize() {
    if (!this.engine || !this.world) {
      return;
    }

    const { World } = Matter;

    this._updateBounds();
    World.remove(this.world, this.walls);
    this._createWalls();
    this._clampAllBodies();
    this._syncDom();
  }

  destroy() {
    this.destroyed = true;

    if (this.fallDelayTimeout !== null) {
      window.clearTimeout(this.fallDelayTimeout);
      this.fallDelayTimeout = null;
    }

    if (this.runner) {
      Matter.Runner.stop(this.runner);
    }

    if (this.engine) {
      Matter.Events.off(this.engine, "afterUpdate", this._onAfterUpdate);
    }

    if (this.mouseConstraint) {
      Matter.Events.off(this.mouseConstraint, "startdrag", this._onStartDrag);
      Matter.Events.off(this.mouseConstraint, "enddrag", this._onEndDrag);
    }

    if (this.world) {
      if (this.mouseConstraint) {
        Matter.World.remove(this.world, this.mouseConstraint);
      }

      if (this.walls.length) {
        Matter.World.remove(this.world, this.walls);
      }

      this.tagPairs.forEach(({ body }) => {
        Matter.World.remove(this.world, body);
      });

      Matter.World.clear(this.world, false);
      Matter.Engine.clear(this.engine);
    }

    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();

    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.engine = null;
    this.world = null;
    this.runner = null;
    this.mouse = null;
    this.mouseConstraint = null;
    this.walls = [];
    this.tagPairs = [];

    if (this.canvas) {
      this.canvas.style.touchAction = "";
    }
  }
}

function initTagClouds() {
  document.querySelectorAll(".tag-canvas").forEach((canvas) => {
    const cloud = new TagCloud(canvas);
    cloud.init();
    canvas.tagCloud = cloud;
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initTagClouds);
} else {
  initTagClouds();
}

export default TagCloud;
